/**
 * Real WebGL 3D Checkers board (react-three-fiber).
 *
 * Same babel-plugin workaround as ChessBoard3D — every element is
 * created with React.createElement directly, never via JSX, so the
 * `@emergentbase/visual-edits` plugin can't inject hyphenated props
 * (`x-line-number`, etc.) that crash R3F's applyProps.
 *
 * Logical state is supplied by CheckersRoom (board / selected /
 * pathSet / targetSet / lastSet / flipped) and `onSquareClick(r, c)`
 * mirrors the 2D board's contract so the existing WebSocket pipeline
 * keeps working.
 */
import { useMemo, useRef, createElement as h } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { makeGrainTexture } from "@/lib/grainTexture";

const SIZE = 8;

const PAL = {
  // Per user iter 63: dark squares + frame recoloured to LIGHT BROWN
  // (was deep walnut #5C3B22).  Matches the chess board palette.
  light:    "#EFE3C2",
  dark:     "#A07A50",
  rim:      "#A07A50",
  rimGold:  "#D9B477",
  hi:       "#FFE096",
  legal:    "#FFE096",
  // piece colours
  black:    "#1A0E08",
  blackInner: "#3D2A1F",
  red:      "#A52E1A",
  redInner: "#D26A4F",
  crown:    "#E8B86A",
};

// World coords: square (r,c) — board is 8x8 centred on origin.  When
// `flipped`, the camera flips, but the data still uses original (r,c)
// indexing.  We render at world position (c - 3.5, 0, r - 3.5).
function squareToWorld(r, c) {
  return { x: c - 3.5, z: r - 3.5 };
}

// ------------------------------------------------------------------
//  Lights — neutral, even illumination so the warm wood reads cleanly.
// ------------------------------------------------------------------
function Lights() {
  return h("group", null,
    h("ambientLight", { intensity: 0.7 }),
    h("directionalLight", {
      position: [6, 12, 4],
      intensity: 0.95,
      castShadow: true,
      "shadow-mapSize": [1024, 1024],
      color: "#FFFFFF",
    }),
    h("directionalLight", { position: [-6, 8, -4], intensity: 0.35, color: "#FFFFFF" }),
  );
}

// ------------------------------------------------------------------
//  Wooden frame underneath the squares — walnut with grain.
// ------------------------------------------------------------------
function BoardFrame() {
  const grain = useMemo(() => makeGrainTexture("walnut-frame", 8, 0.55), []);
  return h("group", { position: [0, -0.04, 0] },
    h("mesh", { receiveShadow: true },
      h("boxGeometry", { args: [9.2, 0.10, 9.2] }),
      h("meshStandardMaterial", {
        color: PAL.rim,
        map: grain,
        metalness: 0.05,
        roughness: 0.85,
      }),
    ),
  );
}

// ------------------------------------------------------------------
//  One square + optional highlight pad.
// ------------------------------------------------------------------
function BoardSquare({ r, c, isLight, isSelected, isPathStep, isLegalTarget, isLastMove, onClick }) {
  const { x, z } = squareToWorld(r, c);
  const darkGrain = useMemo(() => makeGrainTexture("walnut-square", 1, 0.55), []);
  let highlight = null;
  let highlightOpacity = 0.4;
  if (isSelected) { highlight = PAL.hi; highlightOpacity = 0.65; }
  else if (isPathStep) { highlight = PAL.legal; highlightOpacity = 0.5; }
  else if (isLastMove) { highlight = PAL.hi; highlightOpacity = 0.45; }

  const stop = (e) => { e.stopPropagation(); onClick(); };

  const children = [
    h("mesh", { key: "sq", receiveShadow: true, onClick: stop },
      h("boxGeometry", { args: [0.98, 0.06, 0.98] }),
      h("meshStandardMaterial", {
        color: isLight ? PAL.light : PAL.dark,
        map: isLight ? null : darkGrain,
        metalness: 0.05,
        roughness: 0.85,
      }),
    ),
  ];
  if (highlight) {
    children.push(
      h("mesh", { key: "hl", position: [0, 0.034, 0], onClick: stop },
        h("boxGeometry", { args: [0.96, 0.005, 0.96] }),
        h("meshBasicMaterial", { color: highlight, transparent: true, opacity: highlightOpacity }),
      ),
    );
  }
  // Legal-target dot — small flat disc floating above an empty target.
  if (isLegalTarget) {
    children.push(
      h("mesh", { key: "dot", position: [0, 0.05, 0], rotation: [-Math.PI / 2, 0, 0], onClick: stop },
        h("circleGeometry", { args: [0.18, 24] }),
        h("meshBasicMaterial", { color: PAL.legal, transparent: true, opacity: 0.7 }),
      ),
    );
  }
  return h("group", { position: [x, 0, z] }, ...children);
}

// ------------------------------------------------------------------
//  Checker piece — disc with a slight bevel.  Kings get a gold crown
//  ornament stacked on top.
// ------------------------------------------------------------------
function CheckerPiece({ piece, x, z, onClick }) {
  const isBlack = piece === "b" || piece === "B";
  const isKing  = piece === "B" || piece === "R";
  const stop    = (e) => { e.stopPropagation(); onClick(); };

  const baseColor  = isBlack ? PAL.black : PAL.red;
  const ringColor  = isBlack ? PAL.blackInner : PAL.redInner;

  const elements = [
    // Bottom disc (the bulk of the piece).
    h("mesh", { key: "disc", position: [0, 0.13, 0], castShadow: true, receiveShadow: true },
      h("cylinderGeometry", { args: [0.34, 0.36, 0.18, 32] }),
      h("meshStandardMaterial", { color: baseColor, metalness: 0.18, roughness: 0.55 }),
    ),
    // Top inner ring — decorative inset that catches the light, gives
    // the disc its classic checker look.
    h("mesh", { key: "ring", position: [0, 0.221, 0] },
      h("torusGeometry", { args: [0.24, 0.012, 8, 32] }),
      h("meshStandardMaterial", { color: ringColor, metalness: 0.25, roughness: 0.5 }),
    ),
    // Outer rim torus.
    h("mesh", { key: "rim", position: [0, 0.221, 0] },
      h("torusGeometry", { args: [0.34, 0.014, 8, 40] }),
      h("meshStandardMaterial", { color: PAL.rimGold, metalness: 0.85, roughness: 0.32 }),
    ),
  ];
  if (isKing) {
    // Stack a second smaller disc on top to show "kinged".
    elements.push(
      h("mesh", { key: "king-disc", position: [0, 0.32, 0], castShadow: true },
        h("cylinderGeometry", { args: [0.28, 0.30, 0.10, 32] }),
        h("meshStandardMaterial", { color: baseColor, metalness: 0.18, roughness: 0.55 }),
      ),
      h("mesh", { key: "king-rim", position: [0, 0.371, 0] },
        h("torusGeometry", { args: [0.28, 0.012, 8, 40] }),
        h("meshStandardMaterial", { color: PAL.crown, metalness: 0.9, roughness: 0.25 }),
      ),
      // A tiny crown bead in the middle.
      h("mesh", { key: "king-bead", position: [0, 0.395, 0] },
        h("sphereGeometry", { args: [0.06, 16, 16] }),
        h("meshStandardMaterial", { color: PAL.crown, metalness: 0.9, roughness: 0.25 }),
      ),
    );
  }
  return h("group", { position: [x, 0, z], onClick: stop }, ...elements);
}

// ------------------------------------------------------------------
//  Camera position — always at z=+8.  The double-flip bug (camera
//  flip + scene rotation π) cancelled out the seat-aware orientation,
//  so a player sitting as Black was viewing from Red's seat (and
//  therefore seeing Red's pieces nearer).  Per user iter 62: keep the
//  camera fixed at +Z and let the scene's `rotation [0, π if flipped, 0]`
//  handle seat orientation alone.  This guarantees the pieces nearest
//  to the player are the ones they control.
// ------------------------------------------------------------------
function CameraOrient() {
  const { camera } = useThree();
  const last = useRef(false);
  if (!last.current) {
    last.current = true;
    camera.position.set(0, 7, 8);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }
  return null;
}

// ------------------------------------------------------------------
//  Public component
// ------------------------------------------------------------------
export default function CheckersBoard3D({
  board,
  flipped,
  selectedSq,        // [r,c] | null
  pathSet,           // Set<"r,c">  — already-traversed steps in current pick
  targetSet,         // Set<"r,c">  — legal target squares
  lastSet,           // Set<"r,c">  — last-move squares
  onSquareClick,
  onBackdropClick,
  height = 520,
}) {
  const cells = useMemo(() => {
    const out = [];
    for (let r = 0; r < SIZE; r++) {
      for (let c = 0; c < SIZE; c++) {
        const piece = board?.[r]?.[c] || null;
        const isLight = (r + c) % 2 === 0;
        const isSelected = selectedSq && selectedSq[0] === r && selectedSq[1] === c;
        const key = `${r},${c}`;
        const isPathStep = !isSelected && pathSet?.has(key);
        const isLegalTarget = !isSelected && targetSet?.has(key);
        const isLastMove = lastSet?.has(key);
        out.push({ r, c, piece, isLight, isSelected, isPathStep, isLegalTarget, isLastMove });
      }
    }
    return out;
  }, [board, selectedSq, pathSet, targetSet, lastSet]);

  const sceneChildren = [
    h(CameraOrient, { key: "cam" }),
    h(OrbitControls, {
      key: "orbit",
      enableDamping: true,
      dampingFactor: 0.08,
      enablePan: false,
      minDistance: 6,
      maxDistance: 16,
      minPolarAngle: Math.PI * 0.18,
      maxPolarAngle: Math.PI * 0.46,
    }),
    h(Lights, { key: "lights" }),
    h("group", { key: "scene", rotation: [0, flipped ? Math.PI : 0, 0] },
      h(BoardFrame, { key: "frame" }),
      ...cells.map((c) =>
        h(BoardSquare, {
          key: `sq-${c.r}-${c.c}`,
          r: c.r,
          c: c.c,
          isLight: c.isLight,
          isSelected: c.isSelected,
          isPathStep: c.isPathStep,
          isLegalTarget: c.isLegalTarget,
          isLastMove: c.isLastMove,
          onClick: () => onSquareClick(c.r, c.c),
        }),
      ),
      ...cells
        .filter((c) => c.piece)
        .map((c) => {
          const { x, z } = squareToWorld(c.r, c.c);
          return h(CheckerPiece, {
            key: `p-${c.r}-${c.c}`,
            piece: c.piece,
            x, z,
            onClick: () => onSquareClick(c.r, c.c),
          });
        }),
    ),
  ];

  return (
    <div
      data-testid="checkers-board-3d"
      style={{
        width: "100%",
        maxWidth: 720,
        height,
        background: "linear-gradient(180deg,#2A1A0C 0%,#3B2412 100%)",
        border: `1px solid ${PAL.rimGold}`,
        boxShadow: "inset 0 0 0 1px rgba(201,148,80,0.18), 0 8px 30px rgba(0,0,0,0.55)",
      }}
    >
      {h(Canvas,
        {
          shadows: true,
          dpr: [1, 2],
          camera: { position: [0, 7, 8], fov: 42 },
          onPointerMissed: () => onBackdropClick && onBackdropClick(),
        },
        ...sceneChildren,
      )}
    </div>
  );
}
